import type { FulfilmentStatus } from './orders';

/**
 * Kitchen and Bar display rules shared by the KDS, the tests and the server (which re-validates
 * everything). Phase 5 (ADR-020): attributed bumps, current-state timestamps, hints not truth.
 */

export const PRODUCTION_ITEM_STATUSES = ['queued', 'preparing', 'ready', 'cancelled'] as const;
export type ProductionItemStatus = (typeof PRODUCTION_ITEM_STATUSES)[number];

export const TICKET_STATUSES = ['queued', 'in_progress', 'ready', 'served', 'cancelled'] as const;
export type TicketStatus = (typeof TICKET_STATUSES)[number];

/** Owner decision 3: warning and late thresholds, business defaults with per-station overrides. */
export const KDS_DEFAULTS = {
  warnSeconds: 300,
  lateSeconds: 600,
  undoWindowSeconds: 120,
  /** The recall drawer is bounded (refinement 6). */
  recallLimit: 12,
  recallWindowMinutes: 120,
  /** Polling fallbacks; realtime only shortens the wait (refinement 4). */
  kdsPollSeconds: 20,
  posPollSeconds: 60,
} as const;

export interface AgeThresholds {
  warnSeconds: number;
  lateSeconds: number;
}

/** Effective thresholds: a station override beats the business or location setting. */
export function effectiveThresholds(
  settings: AgeThresholds,
  station: { warn_seconds: number | null; late_seconds: number | null } | null | undefined,
): AgeThresholds {
  const warn = station?.warn_seconds ?? settings.warnSeconds;
  const late = Math.max(station?.late_seconds ?? settings.lateSeconds, warn);
  return { warnSeconds: warn, lateSeconds: late };
}

export type AgeBucket = 'fresh' | 'warn' | 'late';

export function ageSeconds(sentAt: string, nowMs: number): number {
  return Math.max(0, Math.floor((nowMs - new Date(sentAt).getTime()) / 1000));
}

export function ageBucket(sentAt: string, nowMs: number, t: AgeThresholds): AgeBucket {
  const s = ageSeconds(sentAt, nowMs);
  if (s >= t.lateSeconds) return 'late';
  if (s >= t.warnSeconds) return 'warn';
  return 'fresh';
}

/** `m:ss` under an hour, `h:mm:ss` after. */
export function formatAge(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  const two = (n: number) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${two(m)}:${two(s)}` : `${m}:${two(s)}`;
}

/** Mirror of `app.ticket_rollup`: cancelled when nothing is live, ready when every live item is. */
export function ticketStatusFromItems(
  items: readonly { status: ProductionItemStatus }[],
): Exclude<TicketStatus, 'served'> {
  const live = items.filter((i) => i.status !== 'cancelled');
  if (live.length === 0) return 'cancelled';
  if (live.every((i) => i.status === 'ready')) return 'ready';
  if (live.some((i) => i.status === 'preparing' || i.status === 'ready')) return 'in_progress';
  return 'queued';
}

/**
 * Mirror of `app.order_fulfilment_rollup` for an order that has been sent: served when every live
 * ticket is served and no kitchen line waits unsent, partially served as soon as one is (Phase 6,
 * ADR-021), ready only when every live ticket is ready and no kitchen line waits unsent; null when
 * nothing is live (unchanged). `hasUnsentLines` counts kitchen lines only (a line with a production
 * route): a line with no route is never prepared, so it never keeps an order waiting (…003200).
 */
export function orderFulfilmentFromTickets(
  tickets: readonly { status: TicketStatus }[],
  hasUnsentLines: boolean,
): Extract<
  FulfilmentStatus,
  'sent' | 'in_progress' | 'partially_ready' | 'ready' | 'partially_served' | 'served'
> | null {
  const live = tickets.filter((t) => t.status !== 'cancelled');
  if (live.length === 0) return null;
  const served = live.filter((t) => t.status === 'served').length;
  const ready = live.filter((t) => t.status === 'ready' || t.status === 'served').length;
  if (served === live.length && !hasUnsentLines) return 'served';
  if (served > 0) return 'partially_served';
  if (ready === live.length && !hasUnsentLines) return 'ready';
  if (ready > 0) return 'partially_ready';
  if (live.some((t) => t.status === 'in_progress')) return 'in_progress';
  return 'sent';
}

/** Owner decision 2: quiet undo only inside the window measured from the current ready time. */
export function canUndo(readyAt: string | null, nowMs: number, windowSeconds: number): boolean {
  if (!readyAt) return false;
  return nowMs - new Date(readyAt).getTime() <= windowSeconds * 1000;
}

/** Ids present now that were not seen before: what the chime and the cancellation alert fire on. */
export function newIds(seen: ReadonlySet<string>, current: readonly string[]): string[] {
  return current.filter((id) => !seen.has(id));
}

/** Refinement 5: the existing priority column first (higher is more urgent), then oldest sent. */
export function sortTickets<T extends { priority: number; sent_at: string }>(
  tickets: readonly T[],
): T[] {
  return [...tickets].sort((a, b) => b.priority - a.priority || a.sent_at.localeCompare(b.sent_at));
}
